/**
 * Tenant Setup · Entities & Divisions
 *
 * Route: /platform/tenants/entities
 * Sidebar: Platform → Tenants → this page (Q3 answer: option A).
 *
 * Lets the admin declare every legal entity / division inside the tenant,
 * tag each with the applicable regulator returns, and edit in place.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Building2, Plus, Save, Trash2, X, Landmark, Shield, ShieldCheck, Award,
  Loader2,
} from "lucide-react";

const NAVY = "#0A1F44";
const GOLD = "#C9A24A";

const ENTITY_TYPES = [
  { code: "parent",         label: "Parent entity" },
  { code: "division",       label: "Division" },
  { code: "subsidiary",     label: "Subsidiary" },
  { code: "branch",         label: "Branch" },
  { code: "shared_service", label: "Shared service" },
];

export default function TenantEntitiesPage() {
  const [entities, setEntities] = useState([]);
  const [catalogue, setCatalogue] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // draft object or null

  const load = useCallback(async () => {
    try {
      const [ents, cat] = await Promise.all([
        api.get("/regulatory/entities"),
        api.get("/regulatory/entities/catalogue"),
      ]);
      setEntities(ents.data.entities || []);
      setCatalogue(cat.data.regulators || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load entities");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => setEditing({
    legal_name: "", division: "", entity_type: "division",
    fsp_number: "", parent_entity_id: "",
    fsca_returns: [], fic_returns: [], pa_returns: [], ombud_scheme: "",
  });

  return (
    <div className="min-h-screen" style={{ background: "#F8F9FA" }} data-testid="tenant-entities-page">
      <header
        className="relative overflow-hidden text-white"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #061633 65%, #020814 100%)` }}
      >
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full" style={{ background: `${GOLD}18` }} />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-10">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: GOLD }}>
            <Building2 className="w-3 h-3" />
            Platform · Tenants · Entities &amp; Divisions
          </div>
          <h1 className="font-display text-3xl lg:text-4xl font-semibold mt-2">Entities &amp; Divisions</h1>
          <p className="mt-3 text-white/70 max-w-2xl text-[13.5px] leading-relaxed">
            Declare every legal entity, division and shared service inside the tenant, and tag which
            regulator returns apply to each. The Regulatory Data Lake scopes every field pull by entity.
          </p>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div className="text-[13px] text-slate-600">
            <strong className="tabular-nums">{entities.length}</strong> entity{entities.length === 1 ? "" : "ies"} configured
          </div>
          {!editing && (
            <button
              type="button"
              onClick={startNew}
              data-testid="btn-new-entity"
              className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-[12px] font-semibold text-white"
              style={{ background: NAVY }}
            >
              <Plus className="w-3.5 h-3.5" /> New entity
            </button>
          )}
        </div>

        {editing && (
          <EntityForm
            draft={editing}
            catalogue={catalogue}
            existingEntities={entities}
            onCancel={() => setEditing(null)}
            onSaved={async () => { setEditing(null); await load(); }}
          />
        )}

        {loading && (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500 text-[13px]">
            <Loader2 className="w-4 h-4 mx-auto animate-spin" />
            Loading…
          </div>
        )}

        {!loading && entities.length === 0 && !editing && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
            <Building2 className="w-6 h-6 mx-auto text-slate-300" />
            <div className="mt-2 text-[13px] font-semibold" style={{ color: NAVY }}>
              No entities configured yet
            </div>
            <div className="text-[12px] text-slate-500 mt-1">
              Add the parent entity first, then divisions and subsidiaries under it.
            </div>
          </div>
        )}

        <div className="space-y-2">
          {entities.map((e) => (
            <EntityRow
              key={e.entity_id}
              entity={e}
              onEdit={() => setEditing(e)}
              onDeleted={load}
              catalogue={catalogue}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Row ─────────────────────────────────────── */

function EntityRow({ entity, onEdit, onDeleted, catalogue }) {
  const del = async () => {
    if (!window.confirm(`Delete "${entity.legal_name}"?`)) return;
    try {
      await api.delete(`/regulatory/entities/${entity.entity_id}`);
      toast.success("Entity deleted");
      onDeleted();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };
  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-4 flex items-start gap-4 flex-wrap"
      data-testid={`entity-row-${entity.entity_id}`}
    >
      <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${NAVY}0F` }}>
        <Building2 className="w-4 h-4" style={{ color: NAVY }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-display text-[15px] font-semibold" style={{ color: NAVY }}>{entity.legal_name}</div>
          <span className="text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded"
            style={{ background: "#F1F5F9", color: "#475569" }}>{entity.entity_type}</span>
          {entity.fsp_number && (
            <span className="text-[10.5px] font-mono text-slate-500">FSP {entity.fsp_number}</span>
          )}
        </div>
        {entity.division && (
          <div className="text-[11.5px] text-slate-500 mt-0.5">{entity.division}</div>
        )}
        <ReturnBadges entity={entity} catalogue={catalogue} />
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button type="button" onClick={onEdit} data-testid={`btn-edit-${entity.entity_id}`}
          className="text-[11.5px] font-semibold text-slate-600 hover:text-slate-900">Edit</button>
        <button type="button" onClick={del} data-testid={`btn-delete-${entity.entity_id}`}
          className="text-[11.5px] font-semibold text-rose-600 hover:text-rose-800 inline-flex items-center gap-1">
          <Trash2 className="w-3 h-3" /> Delete
        </button>
      </div>
    </div>
  );
}

function ReturnBadges({ entity }) {
  const buckets = [
    { key: "fsca_returns", label: "FSCA",  icon: Shield,       colour: "#0A1F44" },
    { key: "fic_returns",  label: "FIC",   icon: ShieldCheck,  colour: "#7C2D12" },
    { key: "pa_returns",   label: "PA",    icon: Landmark,     colour: "#065F46" },
  ];
  const any = buckets.some((b) => (entity[b.key] || []).length) || entity.ombud_scheme;
  if (!any) return <div className="text-[10.5px] text-slate-400 mt-1.5">No returns tagged</div>;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {buckets.map((b) => (
        (entity[b.key] || []).map((r) => (
          <span key={`${b.key}-${r}`} className="inline-flex items-center gap-1 text-[10px] rounded-full px-2 py-0.5"
            style={{ background: `${b.colour}15`, color: b.colour }}>
            <b.icon className="w-2.5 h-2.5" />
            <span className="font-semibold">{b.label}</span>
            <span className="font-mono">{r.replace(/_/g, " ")}</span>
          </span>
        ))
      ))}
      {entity.ombud_scheme && (
        <span className="inline-flex items-center gap-1 text-[10px] rounded-full px-2 py-0.5"
          style={{ background: "#EDE9FE", color: "#5B21B6" }}>
          <Award className="w-2.5 h-2.5" />
          <span className="font-semibold">OMBUD</span>
          <span className="font-mono">{entity.ombud_scheme}</span>
        </span>
      )}
    </div>
  );
}

/* ─── Form ────────────────────────────────────── */

function EntityForm({ draft, catalogue, existingEntities, onCancel, onSaved }) {
  const [d, setD] = useState({ ...draft });
  const [saving, setSaving] = useState(false);
  const eligibleParents = useMemo(
    () => existingEntities.filter((e) => e.entity_id !== d.entity_id),
    [existingEntities, d.entity_id],
  );

  const set = (k, v) => setD((prev) => ({ ...prev, [k]: v }));
  const toggleReturn = (bucket, code) => {
    const list = d[bucket] || [];
    set(bucket, list.includes(code) ? list.filter((c) => c !== code) : [...list, code]);
  };

  const save = async () => {
    if (!d.legal_name.trim()) return toast.error("Legal name is required");
    setSaving(true);
    try {
      await api.put("/regulatory/entities", {
        entity_id: d.entity_id, legal_name: d.legal_name.trim(),
        division: d.division || null, entity_type: d.entity_type,
        fsp_number: d.fsp_number || null,
        parent_entity_id: d.parent_entity_id || null,
        fsca_returns: d.fsca_returns || [], fic_returns: d.fic_returns || [],
        pa_returns: d.pa_returns || [], ombud_scheme: d.ombud_scheme || null,
      });
      toast.success(d.entity_id ? "Entity updated" : "Entity created");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border-2 bg-white p-5 lg:p-6 space-y-4" style={{ borderColor: NAVY }}
      data-testid="entity-form">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-semibold" style={{ color: NAVY }}>
          {d.entity_id ? "Edit entity" : "New entity"}
        </h3>
        <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-700">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Legal name" required>
          <input value={d.legal_name} onChange={(e) => set("legal_name", e.target.value)}
            data-testid="input-legal-name" className={inputCls} placeholder="Nova Bank Retail Ltd." />
        </Field>
        <Field label="Division">
          <input value={d.division || ""} onChange={(e) => set("division", e.target.value)}
            className={inputCls} placeholder="Retail Banking" />
        </Field>
        <Field label="Entity type">
          <select value={d.entity_type} onChange={(e) => set("entity_type", e.target.value)}
            className={inputCls}>
            {ENTITY_TYPES.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
          </select>
        </Field>
        <Field label="FSP number">
          <input value={d.fsp_number || ""} onChange={(e) => set("fsp_number", e.target.value)}
            className={inputCls} placeholder="12345" />
        </Field>
        <Field label="Parent entity">
          <select value={d.parent_entity_id || ""} onChange={(e) => set("parent_entity_id", e.target.value)}
            className={inputCls}>
            <option value="">— None (top level)</option>
            {eligibleParents.map((e) => <option key={e.entity_id} value={e.entity_id}>{e.legal_name}</option>)}
          </select>
        </Field>
        <Field label="Ombud scheme">
          <input value={d.ombud_scheme || ""} onChange={(e) => set("ombud_scheme", e.target.value)}
            className={inputCls} placeholder="OBSSA · OSTI · …" />
        </Field>
      </div>

      <div className="pt-3 border-t border-slate-100 space-y-3">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-400">Applicable returns</div>
        {["FSCA", "FIC", "PA"].map((reg) => {
          const bucket = `${reg.toLowerCase()}_returns`;
          const list = catalogue[reg] || [];
          const selected = d[bucket] || [];
          return (
            <div key={reg} className="flex flex-wrap items-center gap-2">
              <span className="text-[10.5px] font-mono font-semibold w-10" style={{ color: NAVY }}>{reg}</span>
              {list.map((r) => (
                <button
                  key={r.code}
                  type="button"
                  onClick={() => toggleReturn(bucket, r.code)}
                  data-testid={`chip-${reg.toLowerCase()}-${r.code}`}
                  className="text-[11px] px-2 py-1 rounded-full border transition"
                  style={{
                    background: selected.includes(r.code) ? `${NAVY}18` : "white",
                    borderColor: selected.includes(r.code) ? NAVY : "#E2E8F0",
                    color: selected.includes(r.code) ? NAVY : "#475569",
                    fontWeight: selected.includes(r.code) ? 600 : 500,
                  }}
                >
                  {r.label}
                </button>
              ))}
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-end gap-2 pt-3">
        <button type="button" onClick={onCancel}
          className="text-[12px] font-semibold text-slate-600 px-3 py-2">Cancel</button>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="btn-save-entity"
          className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-[12px] font-semibold text-white disabled:opacity-50"
          style={{ background: NAVY }}
        >
          <Save className="w-3.5 h-3.5" />
          {saving ? "Saving…" : (d.entity_id ? "Update" : "Create")}
        </button>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-md border border-slate-300 px-3 py-1.5 text-[13px] focus:outline-none focus:border-slate-600";
function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
        {label} {required && <span className="text-rose-500">*</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
